import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import refreshAuthLogic from './refreshAuthLogic';
import cache from './cache';
import { getAuthToken } from '../authHelper';
import { getBaseUrl } from '../../config';
import { getStore } from '../../redux/storeRef';
import {
  LONG_LOAD_REQUEST_START,
  LONG_LOAD_REQUEST_END,
  LONG_LOAD_SHOW,
  LONG_LOAD_THRESHOLD_MS,
} from '../../redux/reducers/longLoadReducer';

//* disabling the process.env base url.
// const baseURL = process.env.REACT_APP_BASE_URL;
const baseURL = getBaseUrl();

const activeLongLoadTimeouts = new Set();

const clearAllLongLoadTimeouts = () => {
  activeLongLoadTimeouts.forEach(id => clearTimeout(id));
  activeLongLoadTimeouts.clear();
};

/**
 *
 * @param {string} url api path
 * @param {object} [data={}] request paylod
 * @param {object} [config={}] axios config
 * @param {boolean} [ignoreCache=false] flag to enable or disable cache
 *
 */

const fetch = ({ url, data = {}, config = {}, ignoreCache = false }) => {
  try {
    const accessToken = getAuthToken();
    if (baseURL && url && accessToken) {
      const api = axios.create({
        ...(ignoreCache ? {} : { adapter: cache.adapter }),
        baseURL: baseURL,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // set access token and track request start for long-load overlay
      api.interceptors.request.use(
        reqConfig => {
          if (!reqConfig?.headers?.Authorization) {
            reqConfig.headers['Authorization'] = 'Bearer ' + accessToken;
          }
          reqConfig.metadata = { startTime: Date.now() };
          getStore()?.dispatch({ type: LONG_LOAD_REQUEST_START });
          // Show overlay if request exceeds threshold while still in progress
          const longLoadTimeoutId = setTimeout(() => {
            getStore()?.dispatch({ type: LONG_LOAD_SHOW });
          }, LONG_LOAD_THRESHOLD_MS);
          reqConfig.metadata.longLoadTimeoutId = longLoadTimeoutId;
          activeLongLoadTimeouts.add(longLoadTimeoutId);
          return reqConfig;
        },
        error => {
          Promise.reject(error);
        },
      );

      // track request end; first success clears overlay and all pending timers
      const handleRequestEndError = config => {
        const id = config?.metadata?.longLoadTimeoutId;
        if (id) {
          clearTimeout(id);
          activeLongLoadTimeouts.delete(id);
        }
        getStore()?.dispatch({
          type: LONG_LOAD_REQUEST_END,
          payload: { success: false },
        });
      };
      api.interceptors.response.use(
        response => {
          clearAllLongLoadTimeouts();
          getStore()?.dispatch({
            type: LONG_LOAD_REQUEST_END,
            payload: { success: true },
          });
          return response;
        },
        error => {
          handleRequestEndError(error?.config);
          return Promise.reject(error);
        },
      );

      // if 401 (Token expired)
      createAuthRefreshInterceptor(api, refreshAuthLogic);

      return api.post(url, data, config);
    }
  } catch (error) {
    console.log(error);
  }
};

export default fetch;
